'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPrice, formatPercent, getChangeColor } from '@/lib/terminal-format';
import { Sparkline } from './Sparkline';
import { ArrowUpDown } from 'lucide-react';
import type { Stock } from '@/types';

type SortKey = 'name' | 'change' | 'volume';

interface WatchlistQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  sparkline: number[];
}

interface WatchlistProps {
  onSelectSymbol: (s: string) => void;
  onSortedSymbolsChange?: (symbols: string[]) => void;
}

export function Watchlist({ onSelectSymbol, onSortedSymbolsChange }: WatchlistProps) {
  const [sortBy, setSortBy] = useState<SortKey>('name');

  // Fetch portfolio stocks from existing equity-labs API
  const { data: portfolio, isLoading: portfolioLoading } = useQuery<{ stocks: Record<string, Stock> }>({
    queryKey: ['/api/portfolio'],
    queryFn: async () => {
      const res = await fetch('/api/portfolio');
      if (!res.ok) throw new Error('Failed to fetch portfolio');
      return res.json();
    },
  });

  // Extract unique ticker symbols from portfolio (keys are internal IDs, .symbol has the ticker)
  const symbols = useMemo(
    () =>
      portfolio?.stocks
        ? [...new Set(Object.values(portfolio.stocks).map((s) => s.symbol).filter(Boolean))]
        : [],
    [portfolio]
  );

  // Fetch quotes for all portfolio stocks
  const { data: quotesMap, isLoading: quotesLoading } = useQuery<Record<string, WatchlistQuote>>({
    queryKey: ['/api/terminal/watchlist-quotes', symbols.join(',')],
    queryFn: async () => {
      if (symbols.length === 0) return {};
      const results: Record<string, WatchlistQuote> = {};
      await Promise.all(
        symbols.map(async (sym) => {
          try {
            const [quoteRes, chartRes] = await Promise.all([
              fetch(`/api/terminal/quote/${encodeURIComponent(sym)}`),
              fetch(`/api/terminal/chart/${encodeURIComponent(sym)}?range=5d`),
            ]);
            const quote = await quoteRes.json();
            const chart = await chartRes.json();
            const sparkline = Array.isArray(chart) ? chart.map((d: { close: number }) => d.close).filter(Boolean) : [];
            results[sym] = {
              symbol: sym,
              price: quote.price || 0,
              change: quote.change || 0,
              changePercent: quote.changePercent || 0,
              volume: quote.volume || 0,
              sparkline,
            };
          } catch {
            results[sym] = { symbol: sym, price: 0, change: 0, changePercent: 0, volume: 0, sparkline: [] };
          }
        })
      );
      return results;
    },
    enabled: symbols.length > 0,
    refetchInterval: 30000,
  });

  const isLoading = portfolioLoading || quotesLoading;

  const sortedSymbols = useMemo(() => {
    return [...symbols].sort((a, b) => {
      const qa = quotesMap?.[a];
      const qb = quotesMap?.[b];
      if (sortBy === 'name') return a.localeCompare(b);
      if (sortBy === 'change') return (qb?.changePercent || 0) - (qa?.changePercent || 0);
      if (sortBy === 'volume') return (qb?.volume || 0) - (qa?.volume || 0);
      return 0;
    });
  }, [symbols, quotesMap, sortBy]);

  useEffect(() => {
    onSortedSymbolsChange?.(sortedSymbols);
  }, [sortedSymbols, onSortedSymbolsChange]);

  return (
    // Capped height on mobile (stacked layout) so the list scrolls in place; fills the sidebar on md+
    <div className="flex flex-col flex-1 min-h-0 max-h-64 md:max-h-none">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#1F2937]">
        <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Portfolio</span>
        <button
          onClick={() => setSortBy(prev => prev === 'name' ? 'change' : prev === 'change' ? 'volume' : 'name')}
          className="p-1 text-[#6B7280] hover:text-[#E5E7EB] transition-colors"
          title={`Sort by: ${sortBy}`}
        >
          <ArrowUpDown className="w-3 h-3" />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="px-3 py-2 border-b border-[#1F2937]/50">
              <div className="h-3 w-12 mb-1 bg-[#1F2937] animate-pulse rounded" />
              <div className="h-4 w-16 bg-[#1F2937] animate-pulse rounded" />
            </div>
          ))
        ) : sortedSymbols.length === 0 ? (
          <div className="p-4 text-center text-[#6B7280] text-xs">
            Add stocks to your portfolio to see them here
          </div>
        ) : (
          sortedSymbols.map((sym) => {
            const q = quotesMap?.[sym];
            return (
              <div
                key={sym}
                className="flex items-center px-3 py-2 border-b border-[#1F2937]/50 hover:bg-[#1A2035] cursor-pointer transition-colors"
                onClick={() => onSelectSymbol(sym)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-[#E5E7EB]">{sym}</span>
                    <span className="font-mono text-xs tabular-nums text-[#E5E7EB]">
                      {q ? formatPrice(q.price) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <div className="flex items-center gap-1">
                      {q && (
                        <>
                          <span className={`font-mono text-[10px] tabular-nums ${getChangeColor(q.change)}`}>
                            {q.change >= 0 ? '+' : ''}{q.change.toFixed(2)}
                          </span>
                          <span className={`font-mono text-[10px] tabular-nums ${getChangeColor(q.changePercent)}`}>
                            ({formatPercent(q.changePercent)})
                          </span>
                        </>
                      )}
                    </div>
                    {q && q.sparkline.length > 1 && (
                      <Sparkline data={q.sparkline} width={44} height={16} />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
